import React, { useEffect, useMemo, useRef, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { brochuresAPI, publicAPI } from '../services/api';
import './RegistrationForm.css';

// ── HBO defaults ─────────────────────────────────────────────────────────────
const defaultFormState = {
  firstName: '',
  lastName: '',
  address: '',
  dateOfBirth: '',
  placeOfBirth: '',
  telephone: '',
  mobile: '',
  email: '',
  location: '',
  fullPaymentOnce: false,
  fullPaymentDeadline: '',
  installmentPayment: false,
  writtenCancellation: false,
  signedDate: '',
  studentName: '',
  studentSignature: ''
};

// ── Master / Masterclass defaults ─────────────────────────────────────────────
const defaultMasterFormState = {
  nickname: '',
  firstName: '',
  lastName: '',
  address: '',
  dateOfBirth: '',
  placeOfBirth: '',
  telephone: '',
  email: '',
  companyName: '',
  companyFunction: '',
  companyTelephone: '',
  companyEmail: '',
  companyPostAddress: '',
  companyPostcode: '',
  companyVisitAddress: '',
  companyVisitPostcode: '',
  companyKvK: '',
  paidBy: '',
  paymentOnce: false,
  paymentInstallments: false,
  invoiceName: '',
  invoiceTav: '',
  invoiceAddress: '',
  invoicePostcode: '',
  looseModule: '',
  signedDate: '',
  signedCompany: '',
  signedCompanyStamp: '',
  signedStudent: '',
  signedSignature: ''
};

const defaultMasterPrograms = {
  mscFC: false,
  mba: false,
  mbaNaMfc: false,
  masterMediation: false,
  mbaSchoolleiders: false,
  mbaChc: false,
  kortMba: false,
  postHboFC: false,
  postHboController: false,
  postHboSalesController: false,
  postHboLeadership: false,
  masterclassStrategieSales: false,
  masterclassVerandermanagement: false,
  masterclassLeadership: false,
  masterclass3DaagseLeiderschap: false
};

const MASTER_PROGRAMS_5A = [
  { name: 'mscFC', label: 'Master of Science Finance & Control' },
  { name: 'mba', label: 'Master Business Administration' },
  { name: 'mbaNaMfc', label: 'MBA na MFC' },
  { name: 'masterMediation', label: 'Master Mediation' },
  { name: 'mbaSchoolleiders', label: 'Master Business Administration Schoolleiders' },
  { name: 'mbaChc', label: 'Master Business Administration CHC' },
  { name: 'kortMba', label: 'Kort MBA' }
];

const MASTER_PROGRAMS_5B = [
  { name: 'postHboFC', label: 'Post-HBO Financial Controller (1e jr MSc F&C)' },
  { name: 'postHboController', label: 'Post-HBO Controller' },
  { name: 'postHboSalesController', label: 'Post-HBO Sales Controller' },
  { name: 'postHboLeadership', label: 'Post-HBO Leadership & Change' }
];

const MASTERCLASS_PROGRAMS_5C = [
  { name: 'masterclassStrategieSales', label: 'Masterclass Strategie & Sales' },
  { name: 'masterclassVerandermanagement', label: 'Masterclass Verandermanagement' },
  { name: 'masterclassLeadership', label: 'Masterclass Leadership' },
  { name: 'masterclass3DaagseLeiderschap', label: '3-daagse Persoonlijk Leiderschap' }
];

// ── Shared helpers ────────────────────────────────────────────────────────────
const createEducationRow = () => ({
  institution: '',
  opleidingTraining: '',
  diplomaDate: ''
});

const createWorkRow = () => ({
  company: '',
  role: '',
  period: ''
});

const initialEducationRows = Array.from({ length: 5 }, createEducationRow);
const initialWorkRows = Array.from({ length: 4 }, createWorkRow);

const initialHboAttachments = {
  diplomasAndCertificates: [],
  idCopy: null,
  curriculumVitae: null,
  cbbExtract: null,
  passportPhoto: null
};

const initialMasterAttachments = {
  diplomasAndCertificates: [],
  idCopy: null,
  curriculumVitae: null
};

const RegistrationForm = () => {
  const [formType, setFormType] = useState('hbo'); // 'hbo' | 'master' | 'masterclass'

  // ── HBO state ──────────────────────────────────────────────────────────────
  const [form, setForm] = useState(defaultFormState);
  const [availableStudies, setAvailableStudies] = useState([]);
  const [loadingStudies, setLoadingStudies] = useState(true);
  const [selectedStudyIds, setSelectedStudyIds] = useState([]);
  const [educationRows, setEducationRows] = useState(initialEducationRows);
  const [workRows, setWorkRows] = useState(initialWorkRows);
  const [attachments, setAttachments] = useState(initialHboAttachments);

  // ── Master / Masterclass state ──────────────────────────────────────────────
  const [masterForm, setMasterForm] = useState(defaultMasterFormState);
  const [masterPrograms, setMasterPrograms] = useState(defaultMasterPrograms);
  const [masterEducationRows, setMasterEducationRows] = useState(initialEducationRows);
  const [masterWorkRows, setMasterWorkRows] = useState(initialWorkRows);
  const [masterAttachments, setMasterAttachments] = useState(initialMasterAttachments);

  // ── Shared UI state ────────────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const hboFormRef = useRef(null);
  const masterFormRef = useRef(null);

  useEffect(() => {
    const loadStudies = async () => {
      setLoadingStudies(true);
      try {
        const data = await brochuresAPI.getAll();
        const studies = (data || [])
          .filter((item) => ['hbo', 'master', 'training'].includes(item.category))
          .sort((a, b) => {
            const aOrder = Number.isFinite(Number(a?.sortOrder)) ? Number(a.sortOrder) : 0;
            const bOrder = Number.isFinite(Number(b?.sortOrder)) ? Number(b.sortOrder) : 0;
            if (aOrder !== bOrder) return aOrder - bOrder;
            return String(a?.title || '').localeCompare(String(b?.title || ''));
          });
        setAvailableStudies(studies);
      } catch {
        setAvailableStudies([]);
      } finally {
        setLoadingStudies(false);
      }
    };
    loadStudies();
  }, []);

  // ── HBO validation ─────────────────────────────────────────────────────────
  const isFormValid = useMemo(() => {
    const requiredBase = [
      form.firstName,
      form.lastName,
      form.address,
      form.dateOfBirth,
      form.placeOfBirth,
      form.email,
      form.mobile,
      form.location,
      form.signedDate,
      form.studentName,
      form.studentSignature
    ];

    const hasAtLeastOneEducation = educationRows.some((row) => row.institution || row.opleidingTraining || row.diplomaDate);
    const hasAtLeastOneWork = workRows.some((row) => row.company || row.role || row.period);
    const hasPaymentChoice = form.fullPaymentOnce || form.installmentPayment;
    const hasDeadlineWhenFullPayment = !form.fullPaymentOnce || Boolean(form.fullPaymentDeadline);

    return (
      requiredBase.every(Boolean) &&
      selectedStudyIds.length > 0 &&
      hasAtLeastOneEducation &&
      hasAtLeastOneWork &&
      hasPaymentChoice &&
      hasDeadlineWhenFullPayment &&
      form.writtenCancellation
    );
  }, [form, selectedStudyIds, educationRows, workRows, attachments]);

  // ── Master / Masterclass validation ────────────────────────────────────────
  const isMasterFormValid = useMemo(() => {
    const requiredBase = [
      masterForm.firstName,
      masterForm.lastName,
      masterForm.address,
      masterForm.dateOfBirth,
      masterForm.placeOfBirth,
      masterForm.telephone,
      masterForm.email,
      masterForm.paidBy,
      masterForm.signedDate,
      masterForm.signedStudent,
      masterForm.signedSignature
    ];
    const hasAtLeastOneEducation = masterEducationRows.some(
      (row) => row.institution || row.opleidingTraining || row.diplomaDate
    );
    const hasAtLeastOneWork = masterWorkRows.some((row) => row.company || row.role || row.period);
    const hasProgramSelected =
      Object.values(masterPrograms).some(Boolean) || Boolean(masterForm.looseModule.trim());
    const hasPayment = masterForm.paymentOnce || masterForm.paymentInstallments;
    return (
      requiredBase.every(Boolean) &&
      hasAtLeastOneEducation &&
      hasAtLeastOneWork &&
      hasProgramSelected &&
      hasPayment
    );
  }, [masterForm, masterPrograms, masterEducationRows, masterWorkRows, masterAttachments]);

  // ── HBO handlers ───────────────────────────────────────────────────────────
  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setForm((prev) => ({ ...prev, [name]: checked }));
  };

  const updateEducationRow = (index, field, value) => {
    setEducationRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const updateWorkRow = (index, field, value) => {
    setWorkRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const toggleStudySelection = (studyId) => {
    setSelectedStudyIds((prev) => {
      if (prev.includes(studyId)) {
        return prev.filter((id) => id !== studyId);
      }
      return [...prev, studyId];
    });
  };

  // ── Master handlers ────────────────────────────────────────────────────────
  const onMasterChange = (event) => {
    const { name, value } = event.target;
    setMasterForm((prev) => ({ ...prev, [name]: value }));
  };

  const onProgramChange = (name, checked) => {
    setMasterPrograms((prev) => ({ ...prev, [name]: checked }));
  };

  const updateMasterEducationRow = (index, field, value) => {
    setMasterEducationRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const updateMasterWorkRow = (index, field, value) => {
    setMasterWorkRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const switchFormType = (type) => {
    setFormType(type);
    setError('');
    setMessage('');
  };

  // ── HBO submit ─────────────────────────────────────────────────────────────
  const onSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');

    if (!isFormValid) {
      setError('Vul alle verplichte velden in en kies een betaaloptie.');
      return;
    }

    const payload = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      payload.append(key, String(value));
    });
    payload.append('selectedStudyIds', JSON.stringify(selectedStudyIds));
    payload.append('educationRows', JSON.stringify(educationRows));
    payload.append('workRows', JSON.stringify(workRows));

    attachments.diplomasAndCertificates.forEach((file) => {
      payload.append('diplomasAndCertificates', file);
    });

    if (attachments.idCopy) payload.append('idCopy', attachments.idCopy);
    if (attachments.curriculumVitae) payload.append('curriculumVitae', attachments.curriculumVitae);
    if (attachments.cbbExtract) payload.append('cbbExtract', attachments.cbbExtract);
    if (attachments.passportPhoto) payload.append('passportPhoto', attachments.passportPhoto);

    setIsSubmitting(true);
    try {
      const response = await publicAPI.submitRegistration(payload);
      setMessage(response.message || 'Inschrijving succesvol verzonden.');
      setForm(defaultFormState);
      setSelectedStudyIds([]);
      setEducationRows(initialEducationRows);
      setWorkRows(initialWorkRows);
      setAttachments(initialHboAttachments);
      hboFormRef.current?.reset();
    } catch (submitError) {
      setError(submitError.message || 'Verzenden is mislukt. Probeer opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Master / Masterclass submit ────────────────────────────────────────────
  const onSubmitMaster = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');

    if (!isMasterFormValid) {
      setError('Vul alle verplichte velden in en kies een betaaloptie.');
      return;
    }

    const payload = new FormData();
    Object.entries(masterForm).forEach(([key, value]) => payload.append(key, String(value)));
    payload.append('formType', formType);
    payload.append('masterPrograms', JSON.stringify(masterPrograms));
    payload.append('educationRows', JSON.stringify(masterEducationRows));
    payload.append('workRows', JSON.stringify(masterWorkRows));
    masterAttachments.diplomasAndCertificates.forEach((file) =>
      payload.append('diplomasAndCertificates', file)
    );
    if (masterAttachments.idCopy) payload.append('idCopy', masterAttachments.idCopy);
    if (masterAttachments.curriculumVitae) payload.append('curriculumVitae', masterAttachments.curriculumVitae);

    setIsSubmitting(true);
    try {
      const response = await publicAPI.submitRegistration(payload);
      setMessage(response.message || 'Inschrijving succesvol verzonden.');
      setMasterForm(defaultMasterFormState);
      setMasterPrograms(defaultMasterPrograms);
      setMasterEducationRows(initialEducationRows);
      setMasterWorkRows(initialWorkRows);
      setMasterAttachments(initialMasterAttachments);
      masterFormRef.current?.reset();
    } catch (submitError) {
      setError(submitError.message || 'Verzenden is mislukt. Probeer opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="registration-page">
      <Navbar />

      <section className="registration-hero">
        <div className="registration-hero-inner">
          <h1>Online Inschrijfformulier</h1>
          <p>
            Vul onderstaande gegevens volledig in voor je inschrijving bij NLBA. Alle informatie wordt
            direct als gestructureerde aanmelding per e-mail doorgestuurd.
          </p>
        </div>
      </section>

      <section className="registration-section">
        {/* Form type selector */}
        <div className="form-type-selector">
          <button
            type="button"
            className={`form-type-btn${formType === 'hbo' ? ' active' : ''}`}
            onClick={() => switchFormType('hbo')}
          >
            HBO
          </button>
          <button
            type="button"
            className={`form-type-btn${formType === 'master' ? ' active' : ''}`}
            onClick={() => switchFormType('master')}
          >
            Masteropleidingen
          </button>
          <button
            type="button"
            className={`form-type-btn${formType === 'masterclass' ? ' active' : ''}`}
            onClick={() => switchFormType('masterclass')}
          >
            Masterclass
          </button>
        </div>

        {/* ═══════════════ HBO FORM ═══════════════ */}
        {formType === 'hbo' && (
          <form ref={hboFormRef} className="registration-form" onSubmit={onSubmit}>
            <div className="registration-block">
              <h2>1. Persoonsgegevens/student</h2>
              <div className="form-grid">
              <label>
              Volledige voornaam *
              <input name="firstName" value={form.firstName} onChange={onChange} required />
            </label>
            <label>
              Achternaam *
              <input name="lastName" value={form.lastName} onChange={onChange} required />
            </label>
            <label className="span-2">
              Adres *
              <input name="address" value={form.address} onChange={onChange} required />
            </label>
            <label>
              Geboortedatum *
              <input type="date" name="dateOfBirth" value={form.dateOfBirth} onChange={onChange} required />
            </label>
            <label>
              Geboorteplaats *
              <input name="placeOfBirth" value={form.placeOfBirth} onChange={onChange} required />
            </label>
            <label>
              Telefoon
              <input name="telephone" value={form.telephone} onChange={onChange} />
            </label>
            <label>
              Mobiel *
              <input name="mobile" value={form.mobile} onChange={onChange} required />
            </label>
            <label className="span-2">
              E-mail *
              <input type="email" name="email" value={form.email} onChange={onChange} required />
            </label>
            </div>
          </div>

          <div className="registration-block">
            <h2>2. Opleidingen en trainingen</h2>
            <p className="block-intro">Geef een chronologische overzicht van gevolgde opleidingen en trainingen aan.</p>
            <div className="table-like-grid table-3">
              <div className="table-head">Onderwijsinstelling</div>
              <div className="table-head">Opleiding/training</div>
              <div className="table-head">Datum diploma</div>
              {educationRows.map((row, index) => (
                <React.Fragment key={`education-${index}`}>
                  <input
                    value={row.institution}
                    onChange={(event) => updateEducationRow(index, 'institution', event.target.value)}
                    placeholder="Onderwijsinstelling"
                  />
                  <input
                    value={row.opleidingTraining}
                    onChange={(event) => updateEducationRow(index, 'opleidingTraining', event.target.value)}
                    placeholder="Opleiding of training"
                  />
                  <input
                    type="date"
                    value={row.diplomaDate}
                    onChange={(event) => updateEducationRow(index, 'diplomaDate', event.target.value)}
                  />
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="registration-block">
            <h2>3. Werkervaring</h2>
            <div className="table-like-grid table-3">
              <div className="table-head">Naam bedrijf</div>
              <div className="table-head">Functie</div>
              <div className="table-head">Periode</div>
              {workRows.map((row, index) => (
                <React.Fragment key={`work-${index}`}>
                  <input
                    value={row.company}
                    onChange={(event) => updateWorkRow(index, 'company', event.target.value)}
                    placeholder="Naam bedrijf"
                  />
                  <input
                    value={row.role}
                    onChange={(event) => updateWorkRow(index, 'role', event.target.value)}
                    placeholder="Functie"
                  />
                  <input
                    value={row.period}
                    onChange={(event) => updateWorkRow(index, 'period', event.target.value)}
                    placeholder="Bijv. 2021 - 2024"
                  />
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="registration-block">
            <h2>4. Inschrijving studies</h2>
            <p>Kies de studie(s) waarvoor je wilt inschrijven. Deze lijst komt uit actieve brochures.</p>

            {loadingStudies ? (
              <p>Studies laden...</p>
            ) : availableStudies.length === 0 ? (
              <p>Er zijn momenteel geen actieve studies beschikbaar.</p>
            ) : (
              <div className="study-selection-grid">
                {['hbo', 'master', 'training'].map((category) => {
                  const categoryItems = availableStudies.filter((item) => item.category === category);
                  if (categoryItems.length === 0) return null;

                  const categoryLabel =
                    category === 'hbo'
                      ? 'HBO'
                      : category === 'master'
                        ? 'Masteropleidingen'
                        : 'Masterclass';

                  return (
                    <div className="study-selection-group" key={category}>
                      <h3>{categoryLabel}</h3>
                      {categoryItems.map((study) => (
                        <label key={study.id} className="checkbox-row">
                          <input
                            type="checkbox"
                            checked={selectedStudyIds.includes(study.id)}
                            onChange={() => toggleStudySelection(study.id)}
                          />
                          <span>{study.title}</span>
                        </label>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="registration-block">
            <h2>5. Lokatie</h2>
            <div className="radio-row">
              <label>
                <input
                  type="radio"
                  name="location"
                  value="Paramaribo"
                  checked={form.location === 'Paramaribo'}
                  onChange={onChange}
                /> Paramaribo
              </label>
              <label>
                <input
                  type="radio"
                  name="location"
                  value="Nickerie"
                  checked={form.location === 'Nickerie'}
                  onChange={onChange}
                /> Nickerie
              </label>
            </div>
          </div>

          <div className="registration-block">
            <h2>6. Opleidingskosten</h2>
            <p>De kosten voor de opleiding zijn als volgt:</p>
            <ul className="bullet-list">
              <li>Inschrijfgeld: SRD. 500,-</li>
              <li>Leerjaar 1 SRD. 20.000,- (De prijs is inclusief literatuur welke u digitaal ontvangt)</li>
              <li>Leerjaar 2 SRD. 20.000,- (De prijs is inclusief literatuur welke u digitaal ontvangt)</li>
              <li>Leerjaar 3 SRD. 20.000,- (De prijs is inclusief literatuur welke u digitaal ontvangt)</li>
              <li>Leerjaar 4 SRD. 20.000,- (De prijs is inclusief literatuur welke u digitaal ontvangt)</li>
              <li>In het 4e leerjaar betaalt u ook nog Euro 500,- (kosten voor begeleiding, examen en examinatoren uit Nederland).</li>
            </ul>
          </div>

          <div className="registration-block">
            <h2>7. Betaling in termijnen</h2>
            <p>
              NLBA biedt u de mogelijkheid tot betaling in maandelijkse termijnen. Voor aanvang van de colleges dient
              het inschrijfgeld ten bedrage van SRD 1.000,-, alsmede een aanbetaling van SRD 2.000,-, te worden voldaan.
              Het resterende bedrag dient, te rekenen vanaf eind oktober, maandelijks te worden voldaan in termijnen van
              SRD 1.500,-, uiterlijk op de vijfde (5e) van de daaropvolgende maand.
            </p>
            <p>
              De inschrijving voor de BBA is jaarlijks in de maanden juli, augustus en september, waarna het programma
              in oktober van desbetreffend jaar van start gaat.
            </p>
          </div>

          <div className="registration-block">
            <h2>8. Afspraken</h2>
            <p className="required-checkbox-hint">Verplicht aan te vinken: minimaal een betaaloptie en schriftelijke beeindiging.</p>
            <label className="checkbox-row">
              <input type="checkbox" checked readOnly />
              <span>De inschrijving is pas definitief als de betaling van het inschrijfgeld is voldaan.</span>
            </label>
            <label className="checkbox-row">
              <input type="checkbox" checked readOnly />
              <span>Indien men in verzuim is met betaling van het collegegeld, wordt geen toegang verleend tot colleges en ELO.</span>
            </label>
            <label className="checkbox-row">
              <input
                type="checkbox"
                name="fullPaymentOnce"
                checked={form.fullPaymentOnce}
                onChange={onCheckboxChange}
              />
              <span>Ik betaal het volledige collegegeld in een keer op uiterlijk: <strong>(verplicht: kies minimaal een betaaloptie)</strong></span>
            </label>
            <input
              className="inline-date"
              type="date"
              name="fullPaymentDeadline"
              value={form.fullPaymentDeadline}
              onChange={onChange}
              disabled={!form.fullPaymentOnce}
            />
            <label className="checkbox-row">
              <input
                type="checkbox"
                name="installmentPayment"
                checked={form.installmentPayment}
                onChange={onCheckboxChange}
              />
              <span>Ik wil graag in aanmerking komen voor maandelijkse termijnen (zie punt 7). <strong>(verplicht: kies minimaal een betaaloptie)</strong></span>
            </label>
            <label className="checkbox-row">
              <input
                type="checkbox"
                name="writtenCancellation"
                checked={form.writtenCancellation}
                onChange={onCheckboxChange}
              />
              <span>Bij beeindiging dient dit schriftelijk te worden gemeld; bij uitblijven loopt de betaling door. <strong>(verplicht)</strong></span>
            </label>
          </div>

          <div className="registration-block">
            <h2>Ondertekening</h2>
            <p>
              Ondergetekende gaat akkoord met deze inschrijving en verklaart akkoord te zijn met de algemene voorwaarden
              van Netherlands Business Academy (KvK Breda).
            </p>

            <div className="form-grid">
              <label>
                Datum *
                <input type="date" name="signedDate" value={form.signedDate} onChange={onChange} required />
              </label>
              <label>
                Student *
                <input name="studentName" value={form.studentName} onChange={onChange} required />
              </label>
              <label className="span-2">
                Handtekening (naam invullen) *
                <input name="studentSignature" value={form.studentSignature} onChange={onChange} required />
              </label>
            </div>
          </div>

          <div className="registration-block">
            <h2>Bijlagen</h2>
            <p>Dit formulier volledig ingevuld met:</p>
            <div className="form-grid">
              <label className="span-2">
                  Kopie diploma's en certificaten van genoemde opleidingen (optioneel)
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  multiple
                  onChange={(event) => setAttachments((prev) => ({ ...prev, diplomasAndCertificates: Array.from(event.target.files || []) }))}
                />
              </label>
              <label>
                  Kopie legitimatie (optioneel)
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(event) => setAttachments((prev) => ({ ...prev, idCopy: event.target.files?.[0] || null }))}
                />
              </label>
              <label>
                  Curriculum Vitae (optioneel)
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(event) => setAttachments((prev) => ({ ...prev, curriculumVitae: event.target.files?.[0] || null }))}
                />
              </label>
              <label>
                  Uittreksel CBB (optioneel)
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(event) => setAttachments((prev) => ({ ...prev, cbbExtract: event.target.files?.[0] || null }))}
                />
              </label>
              <label>
                  1 Pasfoto (optioneel)
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  onChange={(event) => setAttachments((prev) => ({ ...prev, passportPhoto: event.target.files?.[0] || null }))}
                />
              </label>
            </div>
          </div>

          {error && <p className="form-feedback form-error">{error}</p>}
          {message && <p className="form-feedback form-success">{message}</p>}

          <button type="submit" className="submit-btn" disabled={!isFormValid || isSubmitting}>
            {isSubmitting ? 'Bezig met verzenden...' : 'Verstuur Inschrijving'}
          </button>
        </form>
        )}

        {/* ═══════════════ MASTER / MASTERCLASS FORM ═══════════════ */}
        {(formType === 'master' || formType === 'masterclass') && (
          <form ref={masterFormRef} className="registration-form" onSubmit={onSubmitMaster}>

            {/* 1. Persoonsgegevens */}
            <div className="registration-block">
              <h2>1. Persoonsgegevens/student</h2>
              <div className="form-grid">
                <label>
                  Roepnaam
                  <input name="nickname" value={masterForm.nickname} onChange={onMasterChange} />
                </label>
                <label>
                  Volledige voornaam *
                  <input name="firstName" value={masterForm.firstName} onChange={onMasterChange} required />
                </label>
                <label>
                  Achternaam *
                  <input name="lastName" value={masterForm.lastName} onChange={onMasterChange} required />
                </label>
                <label className="span-2">
                  Adres *
                  <input name="address" value={masterForm.address} onChange={onMasterChange} required />
                </label>
                <label>
                  Geboortedatum *
                  <input type="date" name="dateOfBirth" value={masterForm.dateOfBirth} onChange={onMasterChange} required />
                </label>
                <label>
                  Geboorteplaats *
                  <input name="placeOfBirth" value={masterForm.placeOfBirth} onChange={onMasterChange} required />
                </label>
                <label>
                  Telefoon *
                  <input name="telephone" value={masterForm.telephone} onChange={onMasterChange} required />
                </label>
                <label>
                  E-mail *
                  <input type="email" name="email" value={masterForm.email} onChange={onMasterChange} required />
                </label>
              </div>
            </div>

            {/* 2. Bedrijfsgegevens */}
            <div className="registration-block">
              <h2>
                2. Bedrijfsgegevens/contractant
                <span className="optional-label">(indien van toepassing)</span>
              </h2>
              <div className="form-grid">
                <label>
                  Naam bedrijf
                  <input name="companyName" value={masterForm.companyName} onChange={onMasterChange} />
                </label>
                <label>
                  Functie
                  <input name="companyFunction" value={masterForm.companyFunction} onChange={onMasterChange} />
                </label>
                <label>
                  Telefoon bedrijf
                  <input name="companyTelephone" value={masterForm.companyTelephone} onChange={onMasterChange} />
                </label>
                <label>
                  E-mail bedrijf
                  <input type="email" name="companyEmail" value={masterForm.companyEmail} onChange={onMasterChange} />
                </label>
                <label className="span-2">
                  Postadres
                  <input name="companyPostAddress" value={masterForm.companyPostAddress} onChange={onMasterChange} />
                </label>
                <label>
                  Postcode/Plaats
                  <input name="companyPostcode" value={masterForm.companyPostcode} onChange={onMasterChange} />
                </label>
                <label className="span-2">
                  Bezoekadres
                  <input name="companyVisitAddress" value={masterForm.companyVisitAddress} onChange={onMasterChange} />
                </label>
                <label>
                  Postcode/Plaats (bezoekadres)
                  <input name="companyVisitPostcode" value={masterForm.companyVisitPostcode} onChange={onMasterChange} />
                </label>
                <label>
                  Inschrijvingsnummer KvK
                  <input name="companyKvK" value={masterForm.companyKvK} onChange={onMasterChange} />
                </label>
              </div>
            </div>

            {/* 3. Opleidingen en trainingen */}
            <div className="registration-block">
              <h2>3. Opleidingen en trainingen</h2>
              <p className="block-intro">
                Geef een chronologische overzicht van gevolgde opleidingen aan HBO en/of universitaire instellingen en
                trainingen. Vergeet niet een kopie van uw certificaten / diploma's te sturen naar{' '}
                <strong>studentenregistratie_ald@outlook.com</strong>.
              </p>
              <div className="table-like-grid table-3">
                <div className="table-head">Onderwijsinstelling</div>
                <div className="table-head">Opleiding/training</div>
                <div className="table-head">Datum diploma</div>
                {masterEducationRows.map((row, index) => (
                  <React.Fragment key={`medu-${index}`}>
                    <input
                      value={row.institution}
                      onChange={(e) => updateMasterEducationRow(index, 'institution', e.target.value)}
                      placeholder="Onderwijsinstelling"
                    />
                    <input
                      value={row.opleidingTraining}
                      onChange={(e) => updateMasterEducationRow(index, 'opleidingTraining', e.target.value)}
                      placeholder="Opleiding of training"
                    />
                    <input
                      type="date"
                      value={row.diplomaDate}
                      onChange={(e) => updateMasterEducationRow(index, 'diplomaDate', e.target.value)}
                    />
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* 4. Werkervaring */}
            <div className="registration-block">
              <h2>4. Werkervaring</h2>
              <div className="table-like-grid table-3">
                <div className="table-head">Naam bedrijf</div>
                <div className="table-head">Functie</div>
                <div className="table-head">Periode</div>
                {masterWorkRows.map((row, index) => (
                  <React.Fragment key={`mwork-${index}`}>
                    <input
                      value={row.company}
                      onChange={(e) => updateMasterWorkRow(index, 'company', e.target.value)}
                      placeholder="Naam bedrijf"
                    />
                    <input
                      value={row.role}
                      onChange={(e) => updateMasterWorkRow(index, 'role', e.target.value)}
                      placeholder="Functie"
                    />
                    <input
                      value={row.period}
                      onChange={(e) => updateMasterWorkRow(index, 'period', e.target.value)}
                      placeholder="Bijv. 2021 - 2024"
                    />
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* 5A + 5B: Master programma's (alleen bij Masteropleidingen) */}
            {formType === 'master' && (
              <>
                <div className="registration-block">
                  <h2>5A. Inschrijving Master</h2>
                  <div className="program-checkbox-grid">
                    {MASTER_PROGRAMS_5A.map((p) => (
                      <label key={p.name} className="checkbox-row">
                        <input
                          type="checkbox"
                          checked={masterPrograms[p.name]}
                          onChange={(e) => onProgramChange(p.name, e.target.checked)}
                        />
                        <span>{p.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="registration-block">
                  <h2>5B. Inschrijving Post-HBO</h2>
                  <div className="program-checkbox-grid">
                    {MASTER_PROGRAMS_5B.map((p) => (
                      <label key={p.name} className="checkbox-row">
                        <input
                          type="checkbox"
                          checked={masterPrograms[p.name]}
                          onChange={(e) => onProgramChange(p.name, e.target.checked)}
                        />
                        <span>{p.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* 5C: Masterclass programma's (alleen bij Masterclass) */}
            {formType === 'masterclass' && (
              <div className="registration-block">
                <h2>5C. Inschrijving Masterclass</h2>
                <div className="program-checkbox-grid">
                  {MASTERCLASS_PROGRAMS_5C.map((p) => (
                    <label key={p.name} className="checkbox-row">
                      <input
                        type="checkbox"
                        checked={masterPrograms[p.name]}
                        onChange={(e) => onProgramChange(p.name, e.target.checked)}
                      />
                      <span>{p.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* 5C / 5D: Losse modules */}
            <div className="registration-block">
              <h2>{formType === 'masterclass' ? '5D.' : '5C.'} Inschrijving Losse module(s)</h2>
              <div className="form-grid">
                <label className="span-2">
                  Losse module(s)
                  <input
                    name="looseModule"
                    value={masterForm.looseModule}
                    onChange={onMasterChange}
                    placeholder="Vul in welke losse module(s) u wilt volgen..."
                  />
                </label>
              </div>
            </div>

            {/* 6. Betaling door */}
            <div className="registration-block">
              <h2>6. Betaling door *</h2>
              <div className="radio-row">
                <label>
                  <input
                    type="radio"
                    name="paidBy"
                    value="student"
                    checked={masterForm.paidBy === 'student'}
                    onChange={onMasterChange}
                  />{' '}
                  Student
                </label>
                <label>
                  <input
                    type="radio"
                    name="paidBy"
                    value="bedrijf"
                    checked={masterForm.paidBy === 'bedrijf'}
                    onChange={onMasterChange}
                  />{' '}
                  Bedrijf
                </label>
              </div>
            </div>

            {/* 6A. Betaling */}
            <div className="registration-block">
              <h2>6A. Betaling *</h2>
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={masterForm.paymentOnce}
                  onChange={(e) => setMasterForm((prev) => ({ ...prev, paymentOnce: e.target.checked }))}
                />
                <span>In 1 keer</span>
              </label>
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={masterForm.paymentInstallments}
                  onChange={(e) => setMasterForm((prev) => ({ ...prev, paymentInstallments: e.target.checked }))}
                />
                <span>Termijnbetalingen</span>
              </label>
            </div>

            {/* 6B. Factuuradres */}
            <div className="registration-block">
              <h2>
                6B. Factuuradres
                <span className="optional-label">indien afwijkend van studentadres</span>
              </h2>
              <div className="form-grid">
                <label>
                  Naam
                  <input name="invoiceName" value={masterForm.invoiceName} onChange={onMasterChange} />
                </label>
                <label>
                  T.a.v.
                  <input name="invoiceTav" value={masterForm.invoiceTav} onChange={onMasterChange} />
                </label>
                <label>
                  Adres
                  <input name="invoiceAddress" value={masterForm.invoiceAddress} onChange={onMasterChange} />
                </label>
                <label>
                  Postcode/Plaats
                  <input name="invoicePostcode" value={masterForm.invoicePostcode} onChange={onMasterChange} />
                </label>
              </div>
            </div>

            {/* Ondertekening */}
            <div className="registration-block">
              <h2>Ondertekening</h2>
              <p>
                Ondergetekende gaat akkoord met deze inschrijving en verklaart akkoord te zijn met de algemene voorwaarden
                van Netherlands Business Academy (KvK Breda).
              </p>
              <div className="form-grid">
                <label>
                  Datum *
                  <input type="date" name="signedDate" value={masterForm.signedDate} onChange={onMasterChange} required />
                </label>
                <label>
                  Bedrijf
                  <input name="signedCompany" value={masterForm.signedCompany} onChange={onMasterChange} />
                </label>
                <label>
                  Bedrijfsstempel
                  <input name="signedCompanyStamp" value={masterForm.signedCompanyStamp} onChange={onMasterChange} />
                </label>
                <label>
                  Student *
                  <input name="signedStudent" value={masterForm.signedStudent} onChange={onMasterChange} required />
                </label>
                <label className="span-2">
                  Handtekening (naam invullen) *
                  <input name="signedSignature" value={masterForm.signedSignature} onChange={onMasterChange} required />
                </label>
              </div>
            </div>

            {/* Bijlagen */}
            <div className="registration-block">
              <h2>Bijlagen</h2>
              <p>Dit formulier volledig ingevuld met:</p>
              <div className="form-grid">
                <label className="span-2">
                  Kopie diploma's en certificaten van genoemde opleidingen (optioneel)
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    multiple
                    onChange={(e) =>
                      setMasterAttachments((prev) => ({
                        ...prev,
                        diplomasAndCertificates: Array.from(e.target.files || [])
                      }))
                    }
                  />
                </label>
                <label>
                  Kopie legitimatie (bij voorkeur paspoort) (optioneel)
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) =>
                      setMasterAttachments((prev) => ({ ...prev, idCopy: e.target.files?.[0] || null }))
                    }
                  />
                </label>
                <label>
                  Curriculum Vitae (optioneel)
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) =>
                      setMasterAttachments((prev) => ({ ...prev, curriculumVitae: e.target.files?.[0] || null }))
                    }
                  />
                </label>
              </div>
              <p className="block-intro registration-mail-note">
                Opsturen per post of per mail naar:{' '}
                <a href="mailto:studentenregistratie_ald@outlook.com" className="registration-link">
                  studentenregistratie_ald@outlook.com
                </a>
              </p>
            </div>

            {error && <p className="form-feedback form-error">{error}</p>}
            {message && <p className="form-feedback form-success">{message}</p>}

            <button type="submit" className="submit-btn" disabled={!isMasterFormValid || isSubmitting}>
              {isSubmitting ? 'Bezig met verzenden...' : 'Verstuur Inschrijving'}
            </button>
          </form>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default RegistrationForm;